import { GeographyTypeEnum } from '@nocowanie/core/enums/geography-type.enum';
import { format } from '@nocowanie/core/helpers/date.helpers';
import { SearchEngineModel } from '@nocowanie/common-ui/models/search-engine.model';

import { LocationType, LocationTypeEnum } from '@app/enums';
import { environment } from '@app/environments/environment';
import { isNewListing } from '@app/helpers/listing-mapping.helpers';
import { SearchFilters } from '@app/models';
import { AutocompleteItemModel } from '@app/models/elastic-search/autocomplete-item.model';

import {
    extractGeoIdAndTypeFromAutocompleteItem,
    findAutocompleteItemByName,
    GeoLocationData,
} from './autocomplete.helpers';
import { filterUnsupportedFilters } from './listing.helpers';
import { mapSearchStateToUrlParams } from './search-params.helpers';

export interface ResolvedLocationData {
    locationData: GeoLocationData | null;
    resolvedItem: AutocompleteItemModel | undefined;
    updatedLocation: string;
    earlyReturn?: boolean;
}

export interface ResolveLocationDataParams {
    lastSelectedLocation: GeoLocationData | null;
    searchEngineData: SearchEngineModel;
    items: AutocompleteItemModel[] | null | undefined;
    handleItemSelection: (item: AutocompleteItemModel, silent?: boolean) => boolean;
    fallbackLocationType: LocationType | null | undefined;
}

/**
 * Determines the best matching autocomplete item and location data for a submit event.
 * Covers three resolution strategies:
 * 1. A location was previously selected — find matching item by geoId+locationType.
 * 2. The form carries a geoId but no selection — find item by geoId or create a fallback.
 * 3. The form carries only a text location — find item by name or fall back to the first item.
 */
export function resolveLocationData({
    lastSelectedLocation,
    searchEngineData,
    items,
    handleItemSelection,
    fallbackLocationType,
}: ResolveLocationDataParams): ResolvedLocationData {
    let locationData: ResolvedLocationData['locationData'] = lastSelectedLocation;
    let resolvedItem: AutocompleteItemModel | undefined;
    let updatedLocation = searchEngineData.location;

    if (locationData) {
        resolvedItem = items?.find(item => {
            const itemLocationData = extractGeoIdAndTypeFromAutocompleteItem(item);
            return (
                itemLocationData &&
                locationData &&
                itemLocationData.geoId === locationData.geoId &&
                itemLocationData.locationType === locationData.locationType
            );
        });
        if (resolvedItem) {
            updatedLocation = resolvedItem.name;
        }
    } else if (searchEngineData.geoId) {
        resolvedItem = items?.find(item => {
            const itemLocationData = extractGeoIdAndTypeFromAutocompleteItem(item);
            return itemLocationData?.geoId === searchEngineData.geoId;
        });

        if (resolvedItem) {
            locationData = extractGeoIdAndTypeFromAutocompleteItem(resolvedItem);
            updatedLocation = resolvedItem.name;
        } else {
            locationData = {
                geoId: searchEngineData.geoId,
                locationType: fallbackLocationType || LocationTypeEnum.City,
            };
        }
    } else if (items && items.length > 0 && searchEngineData.location) {
        const matchingItem = findAutocompleteItemByName(items, searchEngineData.location);

        if (!matchingItem) {
            resolvedItem = items[0];
            const isAccommodation = handleItemSelection(resolvedItem, true);
            if (isAccommodation) {
                return { locationData: null, resolvedItem, updatedLocation, earlyReturn: true };
            }
            updatedLocation = resolvedItem.name;
            locationData = extractGeoIdAndTypeFromAutocompleteItem(resolvedItem);
        } else {
            resolvedItem = matchingItem;
            locationData = extractGeoIdAndTypeFromAutocompleteItem(matchingItem);
        }
    }

    return { locationData, resolvedItem, updatedLocation };
}

export function formatSubmittedDates(finalData: SearchEngineModel) {
    return {
        checkIn: finalData.dates.checkInDate
            ? format(finalData.dates.checkInDate, environment.API_DATE_FORMAT)
            : null,
        checkOut: finalData.dates.checkOutDate
            ? format(finalData.dates.checkOutDate, environment.API_DATE_FORMAT)
            : null,
    };
}

export function navigateToLocationListing({
    resolvedItem,
    finalData,
    filters,
    setCookie,
}: {
    resolvedItem: AutocompleteItemModel;
    finalData: SearchEngineModel;
    filters: Partial<SearchFilters>;
    setCookie: (data: SearchEngineModel) => void;
}): void {
    setCookie(finalData);
    const { checkIn: checkInDate, checkOut: checkOutDate } = formatSubmittedDates(finalData);

    const searchParams = mapSearchStateToUrlParams(
        {
            checkInDate,
            checkOutDate,
            guests: {
                adultsCount: finalData.guests.adultsCount,
                childrenCount: finalData.guests.childrenCount,
                childrenAges: finalData.guests.childrenAges || [],
                guestCount: finalData.guests.adultsCount + finalData.guests.childrenCount,
            },
            flexDates: finalData.flexDates,
        },
        filterUnsupportedFilters(filters),
    );

    const qs = searchParams.toString();
    window.location.href = qs ? `${resolvedItem.path}?${qs}` : resolvedItem.path;
}

export function isLocationListingNavigation(
    lastSelectedLocation: GeoLocationData | null,
    resolvedItem: AutocompleteItemModel | null | undefined,
    ssrUrlList?: string[],
) {
    return (
        !!lastSelectedLocation &&
        !!resolvedItem?.path &&
        (resolvedItem.type === GeographyTypeEnum.city ||
            resolvedItem.type === GeographyTypeEnum.voivodeship ||
            resolvedItem.type === GeographyTypeEnum.region) &&
        isNewListing(resolvedItem.path, ssrUrlList)
    );
}
